/**
 * Channel Performance — a compact table that ranks acquisition sources
 * (Site Shariti vs Layan) by conversion rate at each funnel step.
 *
 * Visible on the admin Dashboard only; helps the back-office decide
 * where to invest acquisition budget.
 */
import { motion } from "framer-motion";
import { Briefcase, Globe, TrendingUp, Workflow } from "lucide-react";
import { useEffect, useState } from "react";

import { api } from "../../lib/api";

const CHANNEL_META = {
    "Site Shariti": { Icon: Globe, tone: "bg-orange-50 text-orange-700 border-orange-200" },
    "Layan": { Icon: Briefcase, tone: "bg-blue-50 text-blue-700 border-blue-200" },
    "Autre": { Icon: Workflow, tone: "bg-slate-50 text-slate-700 border-slate-200" },
};

function rateTone(rate) {
    // Color coding by performance band
    if (rate >= 50) return "text-emerald-600";
    if (rate >= 25) return "text-amber-600";
    if (rate > 0) return "text-rose-600";
    return "text-slate-300";
}

function rateBarTone(rate) {
    if (rate >= 50) return "bg-emerald-500";
    if (rate >= 25) return "bg-amber-500";
    if (rate > 0) return "bg-rose-500";
    return "bg-slate-200";
}


export default function ChannelPerformance({ scope = "active" }) {
    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        async function load() {
            setLoading(true);
            try {
                const { data } = await api.get("/dashboard/channel-performance", { params: { scope } });
                if (active) setChannels(data?.channels || []);
            } catch (_e) {
                if (active) setChannels([]);
            } finally {
                if (active) setLoading(false);
            }
        }
        load();
        return () => { active = false; };
    }, [scope]);

    if (loading) {
        return (
            <div className="card-soft p-5 animate-pulse h-44 bg-shariti-cream" data-testid="channel-performance-loading" />
        );
    }
    if (channels.length === 0) {
        return null;
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="card-soft p-5 md:p-6"
            data-testid="channel-performance"
        >
            <header className="flex items-end justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                        <TrendingUp className="h-3 w-3" /> Performance par canal
                    </div>
                    <h2 className="mt-2 text-xl md:text-2xl font-black tracking-tight text-shariti-black">
                        Quel canal vous rapporte le plus de recrues ?
                    </h2>
                    <p className="mt-0.5 text-xs font-medium text-slate-500">
                        Volume reçu, taux de passage à chaque étape, et conversion finale par source.
                    </p>
                </div>
            </header>

            <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="channel-perf-table">
                    <thead>
                        <tr className="text-[10px] font-bold uppercase tracking-widest text-slate-400 text-left">
                            <th className="py-2 pr-4">Canal</th>
                            <th className="py-2 px-3 text-right">Volume</th>
                            <th className="py-2 px-3 text-right">Pré-rec OK</th>
                            <th className="py-2 px-3 text-right">Immersion → validé</th>
                            <th className="py-2 px-3 text-right">Recrutement final</th>
                        </tr>
                    </thead>
                    <tbody>
                        {channels.map((c) => {
                            const meta = CHANNEL_META[c.label] || CHANNEL_META["Autre"];
                            const { Icon, tone } = meta;
                            return (
                                <tr
                                    key={c.label}
                                    className="border-t border-shariti-gray hover:bg-shariti-cream/40 transition"
                                    data-testid={`channel-row-${c.label.replace(/\s/g, "-").toLowerCase()}`}
                                >
                                    <td className="py-3 pr-4">
                                        <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border ${tone}`}>
                                            <Icon className="h-3.5 w-3.5" />
                                            <span className="text-xs font-extrabold">{c.label}</span>
                                        </div>
                                    </td>
                                    <td className="py-3 px-3 text-right">
                                        <div className="text-lg font-black text-shariti-black tabular-nums">{c.volume}</div>
                                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                            candidat{c.volume > 1 ? "s" : ""}
                                        </div>
                                    </td>
                                    <RateCell num={c.prerec_ok} denom={c.volume} rate={c.prerec_rate} />
                                    <RateCell num={c.immersion_validated} denom={c.immersion_done} rate={c.immersion_rate} />
                                    <RateCell num={c.hired} denom={c.volume} rate={c.hired_rate} highlight />
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <ChannelLegend />
        </motion.div>
    );
}


function RateCell({ num, denom, rate, highlight = false }) {
    return (
        <td className="py-3 px-3 text-right">
            <div className={`text-lg font-black tabular-nums ${rateTone(rate)} ${highlight ? "" : ""}`}>
                {rate}%
            </div>
            <div className="text-[10px] font-bold tracking-wider text-slate-400 tabular-nums">
                {num} / {denom || 0}
            </div>
            <div className="h-1 mt-1 bg-slate-100 rounded-full overflow-hidden">
                <div
                    className={`h-full ${rateBarTone(rate)} transition-all`}
                    style={{ width: `${Math.min(100, rate)}%` }}
                />
            </div>
        </td>
    );
}


function ChannelLegend() {
    return (
        <div className="mt-4 pt-3 border-t border-shariti-gray text-[10px] font-medium text-slate-500 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-500" /> ≥ 50% bon
            </span>
            <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-500" /> 25–50% à surveiller
            </span>
            <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-rose-500" /> &lt; 25% sous-performant
            </span>
            <span className="ml-auto italic">Trié par taux de recrutement final (le plus rentable d'abord).</span>
        </div>
    );
}
